import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import QRCode from 'react-qr-code';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useLanguage } from '../context/LanguageContext';
import { t } from '../translations/translations';
import '../styles/ticket.css';

const TicketConfirmation = () => {
  const { language } = useLanguage();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchTickets = async () => {
      if (!sessionId) {
        setError(t('ticket.confirmation.noSessionId', language));
        setLoading(false);
        return;
      }

      try {
        console.log(t('ticket.confirmation.fetchingTickets', language) + ':', sessionId);
        const response = await fetch(`/api/get-ticket-by-session/${sessionId}`);
        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error || t('ticket.confirmation.fetchError', language));
        }

        console.log('Tickets received:', data.tickets);
        setTickets(data.tickets);
      } catch (err) {
        console.error(t('ticket.confirmation.errorFetching', language) + ':', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchTickets();
  }, [sessionId, language]);

  if (loading) {
    return (
      <div className="ticket-confirmation">
        <Header isScrolled={true} />
        <div className="ticket-confirmation-container">
          <div className="ticket-confirmation-header">
            <h1>{t('ticket.confirmation.loadingTickets', language)}</h1>
            <p>{t('ticket.confirmation.pleaseWait', language)}</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="ticket-confirmation">
        <Header isScrolled={true} />
        <div className="ticket-confirmation-container">
          <div className="ticket-confirmation-header error">
            <h1>❌ {t('ticket.confirmation.error', language)}</h1>
            <p>{t('ticket.confirmation.couldNotProcess', language)}</p>
            <p className="error-details">Error: {error}</p>
          </div>
          <Link to="/" className="back-home-button">{t('ticket.confirmation.backToHome', language)}</Link>
        </div>
        <Footer />
      </div>
    );
  }

  if (!tickets || tickets.length === 0) {
    return (
      <div className="ticket-confirmation">
        <Header isScrolled={true} />
        <div className="ticket-confirmation-container">
          <div className="ticket-confirmation-header">
            <h1>{t('ticket.confirmation.noTicketsFound', language)}</h1>
            <p>{t('ticket.confirmation.couldNotFindTickets', language)}</p>
          </div>
          <Link to="/" className="back-home-button">{t('ticket.confirmation.backToHome', language)}</Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="ticket-confirmation">
      <Header isScrolled={true} />
      
      <div className="ticket-confirmation-container">
        <div className="ticket-confirmation-header">
          <h1>{t('ticket.confirmation.paymentSuccessful', language)}</h1>
          <p>{tickets.length > 1 ? t('ticket.confirmation.ticketsSent', language) : t('ticket.confirmation.ticketSent', language)}</p>
        </div>

        {tickets.map((ticket, index) => (
          <div key={ticket.ticketId} className="ticket-card">
            <div className="ticket-card-header">
              <h2>{ticket.eventName}</h2>
              <span className="ticket-number">{t('ticket.confirmation.ticketNumber', language)}{ticket.ticketNumber}</span>
            </div>

            <div className="ticket-card-body">
              <div className="ticket-info-grid">
                <div className="ticket-info-item">
                  <span className="label">{t('ticket.confirmation.attendee', language)}</span>
                  <span className="value">{ticket.attendeeName}</span>
                </div>
                <div className="ticket-info-item">
                  <span className="label">{t('ticket.confirmation.date', language)}</span>
                  <span className="value">{ticket.eventDate}</span>
                </div>
                <div className="ticket-info-item">
                  <span className="label">{t('ticket.confirmation.price', language)}</span>
                  <span className="value">{ticket.price} {ticket.currency}</span>
                </div>
                <div className="ticket-info-item">
                  <span className="label">{t('ticket.confirmation.status', language)}</span>
                  <span className="value status-active">{t('ticket.confirmation.active', language)}</span>
                </div>
              </div>

              <div className="ticket-qr">
                <QRCode 
                  value={`https://www.garbary96.com/ticket-verify/${ticket.ticketId}`} 
                  size={150} 
                  level="H"
                />
                <p className="qr-label">{t('ticket.confirmation.scanQRAtVenue', language)}</p>
              </div>

              <div className="ticket-id">
                <span className="label">{t('ticket.confirmation.ticketId', language)}:</span>
                <span className="value">{ticket.ticketId}</span>
              </div>
            </div>
          </div>
        ))}

        <div className="confirmation-footer">
          <div className="important-notice">
            <h3>{t('ticket.confirmation.importantInfo', language)}</h3>
            <ul>
              <li>{tickets.length > 1 ? t('ticket.confirmation.copiesSent', language) : t('ticket.confirmation.copySent', language)} {tickets[0]?.buyerEmail}</li>
              <li>{t('ticket.confirmation.presentQRCode', language)}</li>
              <li>{t('ticket.confirmation.ticketUsedOnce', language)}</li>
              <li>{t('ticket.confirmation.ticketsExpire', language)}</li>
            </ul>
          </div>

          <div className="action-buttons">
            <button onClick={() => window.print()} className="print-button">
              {t('ticket.confirmation.printTickets', language)}
            </button>
            <Link to="/" className="back-home-button">
              {t('ticket.confirmation.backToHomeButton', language)}
            </Link>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default TicketConfirmation;
